import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import WaveBackground from "@/components/site/WaveBackground";
import { submitContact } from "@/lib/api";
import useSEO from "@/lib/useSEO";

const INTEREST_OPTIONS = [
  "Card Payments",
  "POS Systems",
  "Website Design",
  "App Design",
  "Bundle — full setup",
  "Lower my fees",
  "Just exploring",
];

const initial = { name: "", email: "", phone: "", company: "", interest: "Card Payments", message: "" };

export default function Contact() {
  useSEO({
    title: "Contact — Let's reduce your costs and grow your business",
    description: "Get a quote on card payments and lower your fees. Email info@fencodigital.com, call 07426 821449, or message us on WhatsApp. Replies within one working day.",
    path: "/contact",
  });
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [searchParams] = useSearchParams();

  // Pre-fill interest from ?interest=... query string (e.g. from a Bundle CTA)
  useEffect(() => {
    const q = searchParams.get("interest");
    if (q && INTEREST_OPTIONS.includes(q)) {
      setForm((f) => ({ ...f, interest: q }));
    }
  }, [searchParams]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    if (!form.message.trim()) e.message = "Tell us a little about your business.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      setForm(initial);
      toast.success("Message received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
          <div className="max-w-3xl fade-up">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Get in touch</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900 mt-5">
              Let's help you reduce costs<br />
              and grow your business<span className="text-[#FF5A1F]">.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed">
              Tell us a little about your business — we'll reply within one working day.
            </p>
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          {/* INFO */}
          <aside className="lg:col-span-4 space-y-6" data-testid="contact-info-panel">
            <a
              href="mailto:info@fencodigital.com"
              data-testid="contact-email-card"
              className="block bg-white border border-gray-100 rounded-2xl p-7 hover:border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                <Mail className="text-[#FF5A1F]" size={22} />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500">Email</p>
              <p className="font-display text-xl font-bold text-gray-900 mt-2 break-all">
                info@fencodigital.com
              </p>
              <p className="text-sm text-gray-500 mt-2">Replies within one working day.</p>
            </a>

            <a
              href="tel:07426821449"
              data-testid="contact-phone-card"
              className="block bg-[#0a0a0a] text-white rounded-2xl p-7 hover:-translate-y-1 transition-all relative overflow-hidden"
            >
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-[#FF5A1F]/15 blur-3xl rounded-full" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#FF5A1F]/20 flex items-center justify-center mb-4 border border-[#FF5A1F]/30">
                  <Phone className="text-[#FF5A1F]" size={22} />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5A1F]">Phone</p>
                <p className="font-display text-2xl font-extrabold mt-2">07426 821449</p>
                <p className="text-sm text-gray-400 mt-2">Mon–Fri · 9am — 6pm</p>
              </div>
            </a>

            <div
              data-testid="contact-whatsapp-card"
              className="bg-white border border-gray-100 rounded-2xl p-7 hover:border-gray-200 hover:shadow-lg transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 32 32" className="w-6 h-6" fill="#25D366" aria-hidden="true">
                    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.35 3.41 4.345 4.526.616.343 1.318.658 2.005.787.34.072.74.13 1.088.13 1.075 0 2.47-.747 2.815-1.834.13-.388.158-.83.158-1.246 0-.187-.043-.358-.085-.53-.13-.41-1.66-1.116-2.005-1.116l-.03.026Zm-3.193 9.292a10.91 10.91 0 0 1-5.41-1.461l-3.873 1.232 1.262-3.764a10.92 10.92 0 0 1-2.115-6.467c0-6.08 4.945-11.026 11.026-11.026a10.95 10.95 0 0 1 7.79 3.225 10.97 10.97 0 0 1 3.23 7.795c-.002 6.08-4.945 11.025-11.026 11.025h.115l.001.441Zm0-23.927C8.85 2.57 3.014 8.405 3.014 15.474c0 2.265.578 4.47 1.706 6.404L2.013 28.926l7.18-2.276a13.012 13.012 0 0 0 6.226 1.578h.005c7.067 0 12.902-5.836 12.902-12.902 0-3.45-1.346-6.69-3.788-9.135a12.838 12.838 0 0 0-9.13-3.79l.001.169Z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#25D366]">WhatsApp</p>
                  <p className="font-display text-lg font-bold text-gray-900 mt-2">Message Gareth directly</p>
                  <p className="text-sm text-gray-500 mt-1">Pick your enquiry — we'll reply quickly.</p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <a
                  href="https://wa.me/447426821449?text=Hi%2C%20I%27d%20like%20to%20enquire%20how%20I%20can%20lower%20my%20card%20machine%20costs."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-payments"
                  className="flex items-center justify-between gap-3 px-4 py-3 bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5"
                >
                  <span>Lower my card machine costs</span>
                  <ArrowRight size={16} />
                </a>
                <a
                  href="https://wa.me/447426821449?text=Hi%2C%20I%27d%20like%20to%20get%20a%20free%20quote%20from%20Fenco%20Digital."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-quote"
                  className="flex items-center justify-between gap-3 px-4 py-3 bg-white border border-gray-200 hover:border-[#25D366] hover:text-[#25D366] text-gray-900 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5"
                >
                  <span>Get a free quote</span>
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>

            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-7">
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                <MapPin className="text-[#FF5A1F]" size={22} />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500">Coverage</p>
              <p className="font-display text-lg font-bold text-gray-900 mt-2">United Kingdom</p>
              <p className="text-sm text-gray-600 mt-2">On-site installations and remote support across the UK.</p>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-8">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="bg-white border border-gray-100 rounded-3xl p-12 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="text-[#FF5A1F]" size={32} />
                </div>
                <h3 className="font-display text-3xl font-bold text-gray-900">Thanks — message received.</h3>
                <p className="mt-4 text-gray-600 max-w-md mx-auto">
                  Gareth will reply to you directly within one working day. In the meantime, feel free to call us on <a href="tel:07426821449" className="text-[#FF5A1F] font-semibold">07426 821449</a>.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-success-reset"
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-full transition"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-white border border-gray-100 rounded-3xl p-8 md:p-10 shadow-sm"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Full name"
                    required
                    error={errors.name}
                    testid="contact-input-name"
                  >
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      className="form-input"
                      placeholder="Jane Smith"
                      data-testid="contact-input-name-field"
                    />
                  </Field>
                  <Field
                    label="Email"
                    required
                    error={errors.email}
                    testid="contact-input-email"
                  >
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="form-input"
                      placeholder="jane@business.co.uk"
                      data-testid="contact-input-email-field"
                    />
                  </Field>
                  <Field label="Phone (optional)" testid="contact-input-phone">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      className="form-input"
                      placeholder="07000 000000"
                      data-testid="contact-input-phone-field"
                    />
                  </Field>
                  <Field label="Company (optional)" testid="contact-input-company">
                    <input
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      className="form-input"
                      placeholder="Your business"
                      data-testid="contact-input-company-field"
                    />
                  </Field>
                </div>

                <Field label="What are you interested in?" testid="contact-input-interest" className="mt-5">
                  <select
                    value={form.interest}
                    onChange={update("interest")}
                    className="form-input"
                    data-testid="contact-input-interest-field"
                  >
                    <option>Card Payments</option>
                    <option>POS Systems</option>
                    <option>Website Design</option>
                    <option>App Design</option>
                    <option>Bundle — full setup</option>
                    <option>Lower my fees</option>
                    <option>Just exploring</option>
                  </select>
                </Field>

                <Field
                  label="Tell us about your business"
                  required
                  error={errors.message}
                  testid="contact-input-message"
                  className="mt-5"
                >
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    className="form-input resize-none"
                    placeholder="A few words about your business and what you're hoping to improve..."
                    data-testid="contact-input-message-field"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit"
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] disabled:opacity-60 disabled:cursor-not-allowed rounded-full transition-all shadow-lg shadow-orange-500/25 hover:-translate-y-0.5"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send size={18} />
                    </>
                  )}
                </button>

                <p className="mt-5 text-xs text-gray-500">
                  By submitting, you agree to be contacted by Fenco Digital regarding your enquiry. We never share your data.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border-radius: 0.75rem;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          font-family: inherit;
          font-size: 0.95rem;
          color: #0a0a0a;
          transition: all 0.2s ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #FF5A1F;
          box-shadow: 0 0 0 3px rgba(255,90,31,0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, error, children, testid, className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="text-sm font-semibold text-gray-800">
        {label}
        {required && <span className="text-[#FF5A1F] ml-1">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {error && (
        <span className="block text-xs font-semibold text-red-600 mt-1.5" data-testid={`${testid}-error`}>
          {error}
        </span>
      )}
    </label>
  );
}
